import cx from 'classnames';
import { useEffect, useRef } from 'react';
import { COLORS } from '../../constants/colors';
import { URLS } from '../../constants/urls';
import { Button } from '../Button/Button';
import { CloseIcon } from '../CloseIcon/CloseIcon';
import { View, ViewProps } from '../View/View';
import styles from './VideoPlayer.module.scss';

export interface VideoPlayerProps extends Omit<ViewProps<HTMLElement>, 'fullscreen'> {
  videoId: string;
  width?: number | string;
  height?: number | string;
  autoplay?: YT.AutoPlay;
  controls?: YT.Controls;
  fullscreen?: boolean;
  onClose?: () => void;
  onReady?: (event: YT.PlayerEvent) => void;
  onStateChange?: (event: YT.OnStateChangeEvent) => void;
}

export const VideoPlayer = ({
  className,
  videoId,
  width,
  height,
  autoplay,
  controls,
  fullscreen,
  style,
  onClose,
  onReady,
  onStateChange,
  ...props
}: VideoPlayerProps) => {
  const playerYT = useRef<YT.Player>();

  const onCloseButton = () => {
    if (!onClose || !playerYT.current) return;
    playerYT.current.pauseVideo();
    onClose();
  };

  const loadVideo = () => {
    const player: YT.Player = new YT.Player(`youtube-player-${videoId}`, {
      width: '100%',
      height: '100%',
      videoId,
      playerVars: { autoplay, controls, enablejsapi: 1 },
      events: {
        onReady,
        onStateChange,
      },
    });
    playerYT.current = player;
  };

  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = URLS.YOUTUBE_IFRAME_API;

      (window as any).onYouTubeIframeAPIReady = loadVideo;

      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag?.parentNode?.insertBefore(tag, firstScriptTag);
    } else {
      loadVideo();
    }
  }, []);

  return (
    <View
      className={cx(fullscreen && styles.fullscreen, className)}
      style={fullscreen ? style : { ...style, width, height }}
      {...props}
    >
      <div id={`youtube-player-${videoId}`} />
      {onClose && (
        <Button
          variant="icon"
          className={styles.closeButton}
          onClick={onCloseButton}
          style={{ marginLeft: fullscreen ? '100%' : width }}
          tag="click_landing_video_exit"
        >
          <CloseIcon title="close" color={COLORS.WHITE} />
        </Button>
      )}
    </View>
  );
};
